import styled from "@xstyled/styled-components";
import { useMutation } from "@apollo/client";

import { palette } from "@otta/design-tokens";
import { Button, Spacing, Text } from "@otta/design";
import { Icon } from "@otta/icons";
import {
  CreateUserVerificationSessionDocument,
  RedactAllUserVerificationSessionsDocument,
} from "@otta/search/schema";

const SettingDisplayContainer = styled.div`
  border-top: 1px solid ${palette.grayscale.shade200};
  text-align: left;
`;

const SectionHeader = styled(Text)`
  margin: lg 0;
  gap: sm;
  display: flex;
`;

const VerifyButton = styled(Button)`
  width: 100%;
  margin-top: lg;
`;

const RemoveVerificationButton = styled(Text)`
  margin-top: xl;
  text-decoration: underline;
  cursor: pointer;
  user-select: none;
  color: ${palette.brand.red};
`;

export const ProfileVerification = ({
  isVerified,
  canVerify,
}: {
  isVerified: boolean;
  canVerify: boolean;
}) => {
  const [createVerificationSession] = useMutation(
    CreateUserVerificationSessionDocument,
    {
      onCompleted: data => {
        const redirectUrl = data?.createUserVerificationSession?.url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      },
    }
  );

  const [redactVerification, { loading: redactingLoading }] = useMutation(
    RedactAllUserVerificationSessionsDocument
  );

  if (!canVerify && !isVerified) {
    return null;
  }

  return (
    <SettingDisplayContainer data-testid="profile-verification">
      <SectionHeader bold>
        Profile verification
        {isVerified && (
          <Icon icon="verification" style={{ color: "#ACACFF" }} />
        )}
      </SectionHeader>
      {isVerified ? (
        <>
          <Text>
            You are successfully verified and a badge will be shown on your
            profile
          </Text>
          <Spacing>
            {redactingLoading ? (
              <RemoveVerificationButton>
                Removing your data...
              </RemoveVerificationButton>
            ) : (
              <RemoveVerificationButton
                onClick={() => redactVerification()}
                data-analytics-id="remove-verified-badge"
                data-testid="removed-verified-button"
              >
                Remove your verified badge
              </RemoveVerificationButton>
            )}
          </Spacing>
        </>
      ) : (
        <>
          <Text>Help your applications stand out to companies!</Text>
          <Spacing>
            <VerifyButton
              onClick={() => {
                createVerificationSession({
                  variables: { returnPath: "account-settings" },
                });
              }}
              level="primary"
              data-analytics-id="verify-candidate-account"
              data-testid="verify-account-button"
              disabled={false}
            >
              Verify your account now
            </VerifyButton>
          </Spacing>
        </>
      )}
    </SettingDisplayContainer>
  );
};
